import { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
// Use a retry-aware lazy so a stale-deploy ChunkLoadError reloads to the latest
// build instead of white-screening. Aliased to `lazy` to keep call sites simple.
import lazyWithRetry from "./utils/lazyWithRetry";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
const lazy = lazyWithRetry;
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { ToastProvider } from "./context/ToastContext";
import { NotificationProvider } from "./context/NotificationContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ProtectedRoute, AdminRoute } from "./components/Auth";
import LoadingFallback from "./components/LoadingFallback";
import ToastContainer from "./components/common/Toast";
import GlobalAlert from "./components/common/GlobalAlert";
import "./App.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Auth Pages
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
// Public, sidebar-free job/event pages behind shareable links.
const PublicJob = lazy(() => import("./pages/public/PublicJob"));
const PublicEvent = lazy(() => import("./pages/public/PublicEvent"));
const Unsubscribe = lazy(() => import("./pages/public/Unsubscribe"));

// Dashboard Layout and Pages
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const DashboardOverview = lazy(() => import("./pages/dashboard/Overview"));
const Services = lazy(() => import("./pages/dashboard/Services"));
const DashboardJobs = lazy(() => import("./pages/dashboard/DashboardJobs"));
const Applications = lazy(() => import("./pages/dashboard/Applications"));
const Calendar = lazy(() => import("./pages/dashboard/Calendar"));
const Training = lazy(() => import("./pages/dashboard/Training"));
const Tickets = lazy(() => import("./pages/dashboard/Tickets"));
const Messages = lazy(() => import("./pages/dashboard/Messages"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const Events = lazy(() => import("./pages/dashboard/Events"));
const Actions = lazy(() => import("./pages/dashboard/Actions"));
const Timeline = lazy(() => import("./pages/dashboard/Timeline"));
const CourseView = lazy(() => import("./pages/dashboard/CourseView"));
const Notifications = lazy(() => import("./pages/dashboard/Notifications"));
const Feed = lazy(() => import("./pages/dashboard/Feed"));
const SavedJobs = lazy(() => import("./pages/dashboard/SavedJobs"));
const Dezzo = lazy(() => import("./pages/dashboard/Dezzo"));

// Admin Layout and Pages
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStaff = lazy(() => import("./pages/admin/Staff"));
const AdminAccessControl = lazy(() => import("./pages/admin/AccessControl"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminMessages = lazy(() => import("./pages/admin/Messages"));
const AdminTickets = lazy(() => import("./pages/admin/Tickets"));
const AdminJourneys = lazy(() => import("./pages/admin/Journeys"));
const AdminCourses = lazy(() => import("./pages/admin/Courses"));
const AdminCourseDetail = lazy(() => import("./pages/admin/CourseDetail"));
const AdminCourseEnrollments = lazy(() =>
  import("./pages/admin/CourseEnrollments"),
);
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminEventDetail = lazy(() => import("./pages/admin/EventDetail"));
const AdminJobs = lazy(() => import("./pages/admin/Jobs"));
const AdminMeetings = lazy(() => import("./pages/admin/Meetings"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminUserDetail = lazy(() => import("./pages/admin/UserDetail"));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications"));

// Admin Recruitment Pages
const AdminJobPosting = lazy(
  () => import("./pages/admin/recruitment/JobPosting"),
);
const AdminJobListing = lazy(
  () => import("./pages/admin/recruitment/JobListing"),
);
const AdminJobDetail = lazy(
  () => import("./pages/admin/recruitment/JobDetail"),
);
const AdminJobApplications = lazy(
  () => import("./pages/admin/recruitment/JobApplications"),
);
const AdminJobSharing = lazy(
  () => import("./pages/admin/recruitment/JobSharing"),
);

// Admin System Pages
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminActions = lazy(() => import("./pages/admin/Actions"));
const AdminServices = lazy(() => import("./pages/admin/Services"));
const AdminServiceDetail = lazy(() => import("./pages/admin/ServiceDetail"));
const AdminTags = lazy(() => import("./pages/admin/Tags"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminMyAccount = lazy(() => import("./pages/admin/MyAccount"));
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"));
const AdminFollowUps = lazy(() => import("./pages/admin/FollowUps"));

// Task management (in-house — replaces Hidesc)
const AdminMyTasks = lazy(() => import("./pages/admin/tasks/MyTasks"));
const AdminTeamTasks = lazy(() => import("./pages/admin/tasks/TeamTasks"));
const AdminTaskWorkspaces = lazy(() => import("./pages/admin/tasks/Workspaces"));
const AdminTaskWorkspaceDetail = lazy(() =>
  import("./pages/admin/tasks/WorkspaceDetail"),
);
const AdminTaskClients = lazy(() => import("./pages/admin/tasks/Clients"));
const AdminTaskReports = lazy(() => import("./pages/admin/tasks/TaskReports"));
const AdminTaskSettings = lazy(() => import("./pages/admin/tasks/TaskSettings"));
const AdminTaskDetail = lazy(() => import("./pages/admin/tasks/TaskDetailPage"));

function App() {
  const googleClientId =
    import.meta.env.VITE_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID";
  
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <AuthProvider>
          <ToastProvider>
            <NotificationProvider>
              <ChatProvider>
              <ThemeProvider>
                <Router>
                  <ToastContainer />
                  <GlobalAlert />
                  <ChunkErrorBoundary>
                  <Suspense fallback={<LoadingFallback />}>
                    <div id="MODAL"></div>
                    <Routes>
                      {/* Redirect root to login */}
                      <Route
                        path="/"
                        element={<Navigate to="/login" replace />}
                      />

                      {/* Auth Pages - Public */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      {/* Public share links: the plain path shows the
                          listing, the /apply and /register paths arm the
                          apply flow (prompting login/signup when needed). */}
                      <Route path="/jobs/:jobId" element={<PublicJob />} />
                      <Route
                        path="/jobs/:jobId/apply"
                        element={<PublicJob />}
                      />
                      <Route path="/events/:eventId" element={<PublicEvent />} />
                      <Route
                        path="/events/:eventId/register"
                        element={<PublicEvent />}
                      />
                      {/* Email unsubscribe. Public by necessity: the link is
                          opened from a mail client, often long after the email
                          was sent, on a device that was never signed in. */}
                      <Route path="/unsubscribe" element={<Unsubscribe />} />

                      {/* Dashboard Routes - Protected */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<DashboardOverview />} />
                        <Route path="feed" element={<Feed />} />
                        <Route path="timeline" element={<Timeline />} />
                        <Route path="actions" element={<Actions />} />
                        <Route path="jobs" element={<DashboardJobs />} />
                        <Route path="jobs/:jobId" element={<DashboardJobs />} />
                        <Route path="jobs/saved" element={<SavedJobs />} />
                        <Route path="applications" element={<Applications />} />
                        <Route path="calendar" element={<Calendar />} />
                        <Route path="training" element={<Training />} />
                        <Route
                          path="training/:courseId"
                          element={<CourseView />}
                        />
                        <Route path="services" element={<Services />} />
                        <Route path="dezzo" element={<Dezzo />} />
                        <Route path="tickets" element={<Tickets />} />
                        <Route path="tickets/:ticketId" element={<Tickets />} />
                        <Route path="messages" element={<Messages />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="events" element={<Events />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route
                          path="journey"
                          element={<Navigate to="/dashboard/timeline" replace />}
                        />
                        <Route
                          path="goals"
                          element={<Navigate to="/dashboard/timeline" replace />}
                        />
                      </Route>

                      <Route path="/admin/login" element={<AdminLogin />} />

                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <AdminLayout />
                          </AdminRoute>
                        }
                      >
                        <Route index element={<AdminDashboard />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route
                          path="users/:userId"
                          element={<AdminUserDetail />}
                        />
                        <Route path="calendar" element={<AdminCalendar />} />
                        <Route path="messages" element={<AdminMessages />} />
                        <Route path="tickets" element={<AdminTickets />} />
                        <Route
                          path="tickets/:ticketId"
                          element={<AdminTickets />}
                        />
                        <Route path="journeys" element={<AdminJourneys />} />
                        <Route path="actions" element={<AdminActions />} />
                        <Route
                          path="notifications"
                          element={<AdminNotifications />}
                        />
                        <Route path="services" element={<AdminServices />} />
                        <Route
                          path="services/:serviceId"
                          element={<AdminServiceDetail />}
                        />
                        <Route path="courses" element={<AdminCourses />} />
                        <Route
                          path="courses/enrollments"
                          element={<AdminCourseEnrollments />}
                        />
                        <Route
                          path="courses/:courseId"
                          element={<AdminCourseDetail />}
                        />
                        <Route path="events" element={<AdminEvents />} />
                        <Route
                          path="events/:eventId"
                          element={<AdminEventDetail />}
                        />
                        <Route path="jobs" element={<AdminJobs />} />
                        <Route path="meetings" element={<AdminMeetings />} />
                        <Route path="follow-ups" element={<AdminFollowUps />} />
                        <Route path="payments" element={<AdminPayments />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="my-account" element={<AdminMyAccount />} />
                        <Route
                          path="recruitment/post"
                          element={<AdminJobPosting />}
                        />
                        <Route
                          path="recruitment/edit/:jobId"
                          element={<AdminJobPosting />}
                        />
                        <Route
                          path="recruitment/jobs"
                          element={<AdminJobListing />}
                        />
                        <Route
                          path="recruitment/jobs/:jobId"
                          element={<AdminJobDetail />}
                        />
                        <Route
                          path="recruitment/applications"
                          element={<AdminJobApplications />}
                        />
                        <Route
                          path="recruitment/sharing"
                          element={<AdminJobSharing />}
                        />
                        <Route path="reports" element={<AdminReports />} />
                        <Route path="analytics" element={<AdminAnalytics />} />
                        <Route path="audit-logs" element={<AdminAuditLogs />} />

                        {/* Task management (in-house — replaces Hidesc) */}
                        <Route path="tasks/mine" element={<AdminMyTasks />} />
                        <Route path="tasks/team" element={<AdminTeamTasks />} />
                        <Route
                          path="tasks/workspaces"
                          element={<AdminTaskWorkspaces />}
                        />
                        <Route
                          path="tasks/workspaces/:workspaceId"
                          element={<AdminTaskWorkspaceDetail />}
                        />
                        <Route
                          path="tasks/clients"
                          element={<AdminTaskClients />}
                        />
                        <Route
                          path="tasks/reports"
                          element={<AdminTaskReports />}
                        />
                        <Route
                          path="tasks/settings"
                          element={<AdminTaskSettings />}
                        />
                        <Route
                          path="tasks/:taskId"
                          element={<AdminTaskDetail />}
                        />
                        <Route
                          path="tasks"
                          element={<Navigate to="/admin/tasks/mine" replace />}
                        />
                        <Route
                          path="staff"
                          element={
                            <Navigate to="/admin/settings?tab=staff" replace />
                          }
                        />
                        <Route
                          path="access"
                          element={
                            <Navigate to="/admin/settings?tab=access" replace />
                          }
                        />
                        <Route
                          path="tags"
                          element={
                            <Navigate to="/admin/settings?tab=tags" replace />
                          }
                        />
                      </Route>

                      <Route
                        path="*"
                        element={<Navigate to="/dashboard" replace />}
                      />
                    </Routes>
                  </Suspense>
                  </ChunkErrorBoundary>
                </Router>
              </ThemeProvider>
              </ChatProvider>
            </NotificationProvider>
          </ToastProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
